import {
  Box,
  Button,
  Chip,
  FormControl,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { CheckoutContext } from "contexts/Checkout";
import { OrganizationUserContext } from "contexts/Organization";
import { SubscriptionContext } from "contexts/Subscription";
import { useContext, useEffect, useState } from "react";
import { AddOnItemName } from "schemas/dashboard";
import { Plans } from "schemas/payments";

import { useCheckout } from "components/StripeCheckout/useCheckout";

import NotInterestedButton from "../NotInterestedButton";
import PlanStats from "../PlanStats";
import SeeExample from "../SeeExample";

interface Props {
  displayName: string;
  handleBack: () => void;
  noBackButton?: boolean;
  trackSubscription: () => void;
}

const PlanDetailsSettings = ({
  displayName,
  handleBack,
  noBackButton = false,
  trackSubscription,
}: Props) => {
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("md"));
  const { currentOrg } = useContext(OrganizationUserContext);
  const { addOns, plans } = useContext(SubscriptionContext);
  const { setAlert } = useContext(AlertContext);
  const { plan, invoice, splashPageSource } = useContext(CheckoutContext);
  const { canPurchaseMoreEmailVolume, isVaMoneyBack } = useCheckout();

  const canAdjustQuantity =
    plan && plan in addOns && addOns[plan]?.can_purchase_multiple;
  const [quantity, setQuantity] = useState<number | undefined>(undefined);
  const { applyAddOnItem } = useCheckout();

  useEffect(() => {
    if (!quantity && currentOrg?.id && plan && canAdjustQuantity) {
      applyAddOnItem({
        name: plan as AddOnItemName,
        quantity: 1,
      });
      setQuantity(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id, plan, canAdjustQuantity]);

  return (
    <>
      {!isMobileScreen && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {!noBackButton && (
            <Button
              onClick={handleBack}
              sx={{ textTransform: "none", color: "#4A5565" }}
            >
              <Box
                component="i"
                className="fa-regular fa-arrow-left"
                sx={{ mr: 1 }}
              />{" "}
              Back
            </Button>
          )}
          {splashPageSource && (
            <NotInterestedButton trackSubscription={trackSubscription} />
          )}
        </Box>
      )}
      <Grid container alignItems="center">
        {isMobileScreen && !noBackButton && (
          <IconButton
            sx={{ color: "black", fontSize: 13, mr: 1 }}
            onClick={handleBack}
          >
            <Box component="i" className="fa-regular fa-arrow-left" />
          </IconButton>
        )}
        <Typography
          variant="h2"
          component="div"
          sx={{
            color: "black",
            fontSize: { xs: 25, md: 40 },
          }}
        >
          {displayName}{" "}
          {plan === AddOnItemName.partnership_data && <SeeExample />}
        </Typography>
      </Grid>

      <Grid item xs={12} sx={{ mb: 1 }}>
        {isVaMoneyBack ? (
          <Chip label="Money-back guaranteed 💸" color="primary" size="small" />
        ) : (
          <></>
        )}
      </Grid>
      {isVaMoneyBack ? (
        <Typography sx={{ color: "green.main", opacity: 0.8, mb: 3 }}>
          <Box
            component="i"
            className="fa-solid fa-circle-check"
            sx={{ mr: 1 }}
          />
          {plans && (
            <em>
              If you don't earn $
              {Math.round(plans[Plans.VA_YEARLY]?.price * 12)}+ in your first
              year, we'll refund you. No fuss.
            </em>
          )}
        </Typography>
      ) : (
        <Box sx={{ mb: 3 }}>
          <PlanStats plan={plan as Plans} />
        </Box>
      )}

      {canAdjustQuantity && invoice?.id && (
        <>
          <Typography gutterBottom>
            How many do you want to purchase?
          </Typography>
          <FormControl sx={{ minWidth: 120, mb: 2 }}>
            <Select
              sx={{
                backgroundColor: "white",
                borderRadius: 4,
                minWidth: 80,
              }}
              value={quantity || 1}
              label=""
              onChange={async (event) => {
                if (
                  plan === AddOnItemName.email_volume_monthly ||
                  plan === AddOnItemName.email_volume_yearly
                ) {
                  const canPurchaseMore = canPurchaseMoreEmailVolume(
                    event.target.value as number,
                  );
                  if (!canPurchaseMore) {
                    setAlert(
                      `You are only allowed up to ${addOns[plan]?.max_quantity} packages (including your current subscriptions)`,
                      "warning",
                    );
                    return;
                  }
                }
                setQuantity(event.target.value as number);
                await applyAddOnItem({
                  name: plan as AddOnItemName,
                  quantity: event.target.value as number,
                });
              }}
            >
              {Array.from(
                { length: addOns[plan]?.max_quantity },
                (_, i) => i + 1,
              ).map((num) => (
                <MenuItem key={num} value={num}>
                  {num}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </>
      )}
    </>
  );
};

export default PlanDetailsSettings;
